import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useCallback, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { z } from 'zod';

import { fetchSSOOrganizationsByEmail } from '@/api/organization';
import type { Organization } from '@/api/organization/organizationType';
import RateLimit from '@/app/sign-in/RateLimitButton';
import { Button } from '@/components/buttons';
import { businessEmail, useAppForm } from '@/components/Form';
import { FieldLabel } from '@/components/Form/fields';
import { FullPage } from '@/components/page';
import { Link, Text } from '@/components/typography';
import { maxEmailLength } from '@/constants/numbers';
import { useNotification } from '@/providers/Notification';
import { ROUTES } from '@/router';

import SignInPanel from '../SignInPanel';

const SignInSsoEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const { pushNotification } = useNotification();
  const formSchema = z.object({
    email: businessEmail(),
  });

  const altSignInPath = `${ROUTES.signIn.path}${
    searchParams.size > 0 ? `?${searchParams.toString()}` : ''
  }`;

  const redirectToOrg = useCallback(
    (orgSlug: string) => {
      navigate(ROUTES.signInInstanceBySlug(orgSlug, searchParams));
    },
    [navigate, searchParams],
  );

  const form = useAppForm({
    defaultValues: {
      email: '',
    },
    validators: {
      onSubmit: formSchema,
    },
    onSubmit: async ({ value: { email } }) => {
      setOrganizations([]);

      try {
        const result = await fetchSSOOrganizationsByEmail(email);

        if (!result.length) {
          pushNotification({
            type: 'error',
            message: t`We couldn't find any SSO-enabled instances for that email. Try another address or use a different sign-in method.`,
          });
          return;
        }

        if (result.length === 1) {
          redirectToOrg(result[0].slug);
          return;
        }

        setOrganizations(result);
      } catch {
        pushNotification({
          type: 'error',
          message: t`We couldn't look up your instance. Please try again.`,
        });
      }
    },
  });

  return (
    <FullPage title={t`Sign In`}>
      <SignInPanel
        title={t`What's your email address?`}
        description={<Trans>We'll find your instance so you can sign in with SSO.</Trans>}
        footer={
          <Text variant="caption1" color="grey">
            <Trans>
              Prefer a different method?{' '}
              <Link variant="caption1" color="aqua" to={altSignInPath}>
                Sign in another way
              </Link>
            </Trans>
          </Text>
        }
        showGap
      >
        <form
          className="flex flex-col gap-7"
          onSubmit={(event) => {
            event.preventDefault();
            form.handleSubmit();
          }}
        >
          <form.AppField name="email">
            {(field) => (
              <field.Field>
                <FieldLabel htmlFor="sso-sign-in-email">
                  <Trans>Business email</Trans>
                </FieldLabel>
                <field.TextInput
                  id="sso-sign-in-email"
                  inputMode="email"
                  placeholder={t`name@work-email.com`}
                  autoComplete="email"
                  spellCheck={false}
                  autoCapitalize="none"
                  autoFocus
                  maxLength={maxEmailLength}
                  size="large"
                />
                <field.Error />
              </field.Field>
            )}
          </form.AppField>
          <form.AppForm>
            <RateLimit>
              <form.SubmitButton variant="primary" size="large" block>
                <Trans>Continue to your instance</Trans>
              </form.SubmitButton>
            </RateLimit>
          </form.AppForm>
        </form>

        {organizations.length > 1 && (
          <div className="flex flex-col gap-3 pt-4">
            <Text variant="caption1" color="grey" className="text-center">
              <Trans>Select your instance to continue</Trans>
            </Text>
            <div className="flex flex-col gap-3">
              {organizations.map((org: Organization) => (
                <Button
                  key={org.organizationId}
                  size="large"
                  block
                  onClick={() => redirectToOrg(org.slug)}
                >
                  <Trans>Sign in to {org.name}</Trans>
                </Button>
              ))}
            </div>
          </div>
        )}
      </SignInPanel>
    </FullPage>
  );
};

export default SignInSsoEmail;
